import { useState, useEffect } from 'react';
import { AlertTriangle, ShoppingCart, Download } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '../../services/http';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

const ExportCsvButton = ({ path, filename, testid }) => {
  const exportCsv = async () => {
    try {
      const res = await fetch(`${API_URL}${path}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch {
      toast.error("Échec de l'export CSV");
    }
  };
  return (
    <button type="button" onClick={exportCsv} data-testid={testid}
      className="inline-flex items-center gap-1.5 h-8 px-2.5 mb-2 rounded-lg text-[11px] font-semibold text-[#E9CF8E] bg-[#D9B35A]/10 border border-[#D9B35A]/30 hover:bg-[#D9B35A]/20 transition-colors">
      <Download className="w-3 h-3" /> Export CSV
    </button>
  );
};

const CollapsePanel = ({ icon: Icon, title, testid, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl bg-white/[0.02] border border-white/[0.08] mb-4" data-testid={testid}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`${testid}-toggle`}
        className="w-full flex items-center gap-2 px-4 py-3 text-left text-sm font-semibold text-[#E9CF8E] hover:bg-white/[0.03] rounded-xl transition-colors">
        <Icon className="w-4 h-4" /> {title}
        <span className="ml-auto text-xs text-white/40">{open ? 'Réduire' : 'Ouvrir'}</span>
      </button>
      {open && <div className="px-4 pb-4">{children(open)}</div>}
    </div>
  );
};

export const StockoutStatsPanel = () => {
  const [stats, setStats] = useState(null);
  const load = () => {
    fetch(`${API_URL}/api/catalog/admin/stockout-stats`, { headers: getAuthHeaders() })
      .then((r) => r.json()).then((d) => setStats(d.stats || [])).catch(() => setStats([]));
  };
  return (
    <CollapsePanel icon={AlertTriangle} title="Ruptures par territoire (produits les plus touchés)" testid="stockout-stats-panel">
      {() => {
        if (stats === null) { load(); return <p className="text-xs text-white/40">Chargement…</p>; }
        if (!stats.length) return <p className="text-xs text-white/40 py-1">Aucune rupture enregistrée.</p>;
        return (
          <>
          <ExportCsvButton path="/api/catalog/admin/stockout-stats/export" filename="ruptures_territoires" testid="stockout-export-csv" />
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-white/40">
                <th className="py-1.5 pr-2">Produit</th><th className="py-1.5 pr-2">Territoire</th>
                <th className="py-1.5 pr-2 text-center">Nb ruptures</th><th className="py-1.5 pr-2">Dernière rupture</th><th className="py-1.5">État</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s) => (
                <tr key={`${s.product_id}-${s.zone_code}`} className="border-t border-white/[0.06] text-xs" data-testid="stockout-row">
                  <td className="py-2 pr-2 text-white/85">{s.product_name}</td>
                  <td className="py-2 pr-2 text-white/60">{s.zone_code}</td>
                  <td className="py-2 pr-2 text-center font-mono text-amber-300">{s.stockout_count}</td>
                  <td className="py-2 pr-2 text-white/50 font-mono">{fmtDate(s.last_stockout_at)}</td>
                  <td className="py-2">
                    {s.currently_out
                      ? <span className="px-1.5 py-0.5 rounded text-[9px] font-bold text-red-300 bg-red-500/15 border border-red-400/40">EN RUPTURE</span>
                      : <span className="px-1.5 py-0.5 rounded text-[9px] font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-400/30">Disponible</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        );
      }}
    </CollapsePanel>
  );
};

const ReturnCodeSettings = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    fetch(`${API_URL}/api/catalog/admin/return-code-settings`, { headers: getAuthHeaders() })
      .then((r) => r.json()).then(setS).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/catalog/admin/return-code-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ discount_percent: Number(s.discount_percent), validity_hours: Number(s.validity_hours) }),
      });
      if (!res.ok) throw new Error();
      toast.success('Réglages des bons de retour enregistrés');
    } catch {
      toast.error('Échec de l\'enregistrement (remise 1–50 %, validité 1–720 h)');
    } finally {
      setSaving(false);
    }
  };
  if (!s) return null;
  return (
    <div className="mb-3 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08] flex items-center gap-3 flex-wrap" data-testid="return-code-settings">
      <span className="text-[11px] text-white/60 font-semibold">Bons de retour :</span>
      <label className="flex items-center gap-1.5 text-[11px] text-white/50">
        Remise
        <input type="number" min="1" max="50" value={s.discount_percent}
          onChange={(e) => setS({ ...s, discount_percent: e.target.value })}
          data-testid="return-code-percent-input"
          className="h-7 w-14 px-1.5 rounded-md bg-white/[0.05] border border-white/15 text-white text-[11px] text-center" /> % HT
      </label>
      <label className="flex items-center gap-1.5 text-[11px] text-white/50">
        Validité
        <input type="number" min="1" max="720" value={s.validity_hours}
          onChange={(e) => setS({ ...s, validity_hours: e.target.value })}
          data-testid="return-code-hours-input"
          className="h-7 w-16 px-1.5 rounded-md bg-white/[0.05] border border-white/15 text-white text-[11px] text-center" /> h
      </label>
      <button type="button" onClick={save} disabled={saving} data-testid="return-code-settings-save"
        className="h-7 px-2.5 rounded-md text-[10px] font-bold text-black bg-[#D9B35A] hover:bg-[#c9a34a] transition-colors disabled:opacity-50">
        {saving ? '…' : 'Enregistrer'}
      </button>
    </div>
  );
};

export const AbandonedCartsPanel = () => {
  const [data, setData] = useState(null);
  const [conv, setConv] = useState(null);
  const load = () => {
    fetch(`${API_URL}/api/catalog/admin/abandoned-reservations`, { headers: getAuthHeaders() })
      .then((r) => r.json()).then(setData).catch(() => setData({ entries: [], total: 0 }));
    fetch(`${API_URL}/api/catalog/admin/reminder-conversion`, { headers: getAuthHeaders() })
      .then((r) => r.json()).then(setConv).catch(() => {});
  };
  return (
    <CollapsePanel icon={ShoppingCart} title="Paniers abandonnés (réservations expirées sans commande)" testid="abandoned-carts-panel">
      {() => {
        if (data === null) { load(); return <p className="text-xs text-white/40">Chargement…</p>; }
        if (!data.entries?.length) {
          return (
            <>
              <ReturnCodeSettings />
              <p className="text-xs text-white/40 py-1">Aucune réservation expirée tracée pour l'instant.</p>
            </>
          );
        }
        return (
          <>
            <div className="flex items-center justify-between">
              <p className="text-[11px] text-white/50 mb-2">{data.total} réservation(s) expirée(s) au total — relance email automatique (max 1/24 h par organisation)</p>
              <ExportCsvButton path="/api/catalog/admin/abandoned-reservations/export" filename="paniers_abandonnes" testid="abandoned-export-csv" />
            </div>
            <ReturnCodeSettings />
            {conv && conv.reminders_sent > 0 && (
              <div className="mb-3 px-3 py-2 rounded-lg bg-[#8CC63E]/[0.08] border border-[#8CC63E]/25 flex items-center gap-4 flex-wrap" data-testid="reminder-conversion-stats">
                <span className="text-xs text-white/80">
                  Taux de conversion des relances : <strong className="text-[#8CC63E] text-sm" data-testid="conversion-rate">{conv.conversion_rate_percent} %</strong>
                  <span className="text-white/50"> ({conv.converted}/{conv.reminders_sent} relances → commande sous 7 j)</span>
                </span>
                <span className="text-[11px] text-white/50">Bons de retour utilisés : <strong className="text-[#E9CF8E]">{conv.return_codes_used}/{conv.return_codes_issued}</strong></span>
              </div>
            )}
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-white/40">
                  <th className="py-1.5 pr-2">Organisation</th><th className="py-1.5 pr-2">Produit</th>
                  <th className="py-1.5 pr-2">Territoire</th><th className="py-1.5 pr-2 text-center">Qté</th>
                  <th className="py-1.5 pr-2 text-center">Prolong.</th><th className="py-1.5 pr-2">Expirée le</th>
                  <th className="py-1.5">Relancé</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e) => (
                  <tr key={e.id} className="border-t border-white/[0.06] text-xs" data-testid="abandoned-row">
                    <td className="py-2 pr-2 text-white/85">{e.org_name}</td>
                    <td className="py-2 pr-2 text-white/70">{e.product_name}</td>
                    <td className="py-2 pr-2 text-white/60">{e.zone_code}</td>
                    <td className="py-2 pr-2 text-center font-mono text-white/80">{e.quantity}</td>
                    <td className="py-2 pr-2 text-center font-mono text-white/50">{e.extend_count}</td>
                    <td className="py-2 pr-2 text-white/50 font-mono">{fmtDate(e.expired_at)}</td>
                    <td className="py-2">
                      {e.reminded
                        ? <span className="px-1.5 py-0.5 rounded text-[9px] font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-400/30">✉ OUI</span>
                        : <span className="text-[9px] text-white/35">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        );
      }}
    </CollapsePanel>
  );
};
